import React, { useState } from 'react';
import { User, Building2, Settings, Target, Bot } from 'lucide-react';

interface MaturitySegmentDisplayProps {
  opScore: number;
  stratScore: number;
  printMode?: boolean;
}

// Maturity ranking logic with adjusted thresholds for proper band alignment
export function getMaturityRanking(opScore: number, stratScore: number): string {
  // Adjusted thresholds to ensure 15/30 falls into Managed region
  if (opScore >= 24 && stratScore >= 22) return 'Autonomous';
  if (opScore >= 19 && stratScore >= 18) return 'Controlled';
  if (opScore >= 14 && stratScore >= 13) return 'Managed';
  if (opScore >= 9 && stratScore >= 9) return 'Siloed';
  return 'Manual';
}

// Get tier color based on maturity ranking
function getTierColor(tier: string): string {
  switch (tier) {
    case 'Autonomous': return 'text-cyan-300';
    case 'Controlled': return 'text-teal-400';
    case 'Managed': return 'text-blue-400';
    case 'Siloed': return 'text-slate-400';
    case 'Manual': return 'text-gray-500';
    default: return 'text-gray-400';
  }
}

// Get tier description
function getTierDescription(tier: string): string {
  switch (tier) {
    case 'Autonomous': return 'Fully automated with predictive capabilities';
    case 'Controlled': return 'Orchestrated workflow with real-time visibility';
    case 'Managed': return 'Coordinated processes with selective automation';
    case 'Siloed': return 'Departmental tools with minimal integration';
    case 'Manual': return 'Basic processes with limited automation';
    default: return 'Maturity level assessment';
  }
}

export default function MaturitySegmentDisplay({ opScore, stratScore, printMode = false }: MaturitySegmentDisplayProps) {
  const currentTier = getMaturityRanking(opScore, stratScore);
  const [showTooltip, setShowTooltip] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  

  
  // Create 2D heatmap matrix like the reference image
  const createHeatmapGrid = () => {
    const grid = [];
    
    // Strategic maturity scale (Y-axis, 25 down to 6)
    for (let stratY = 25; stratY >= 6; stratY--) {
      const row = [];
      
      // Operational maturity scale (X-axis, 6 to 30)
      for (let opX = 6; opX <= 30; opX++) {
        const tier = getMaturityRanking(opX, stratY);
        // Match current position using rounded values for fractional score handling
        const isCurrentPosition = opX === Math.round(opScore) && stratY === Math.round(stratScore);
        
        // Simplified solid colors per tier
        let bgColor = '';
        switch (tier) {
          case 'Manual': bgColor = 'bg-[#06303b]'; break;
          case 'Siloed': bgColor = 'bg-[#02515e]'; break;
          case 'Managed': bgColor = 'bg-[#0d828d]'; break;
          case 'Controlled': bgColor = 'bg-[#27c4c5]'; break;
          case 'Autonomous': bgColor = 'bg-[#0cf4e9]'; break;
        }
        
        row.push({
          opX,
          stratY,
          tier,
          bgColor,
          isCurrentPosition
        });
      }
      grid.push(row);
    }
    return grid;
  };

  const heatmapGrid = createHeatmapGrid();

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700 shadow-lg">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left side - Tier descriptions with improved spacing */}
        <div className="lg:w-1/3 space-y-4 pr-4">
          <h3 className="text-xl font-semibold mb-8 text-slate-900 dark:text-white">
            Your maturity ranking
          </h3>
          
          <div className="space-y-4">
            {[
              { name: 'Manual', icon: User, description: 'Basic processes with limited automation', color: 'gray' },
              { name: 'Siloed', icon: Building2, description: 'Departmental tools with minimal integration', color: 'slate' },
              { name: 'Managed', icon: Settings, description: 'Coordinated processes with selective automation', color: 'teal' },
              { name: 'Controlled', icon: Target, description: 'Orchestrated workflow with real-time visibility', color: 'green' },
              { name: 'Autonomous', icon: Bot, description: 'Fully automated with predictive capabilities', color: 'cyan' }
            ].map((tier) => {
              const IconComponent = tier.icon;
              const isSelected = tier.name === currentTier;
              const getColorClasses = (color: string, selected: boolean) => {
                if (selected) {
                  switch (color) {
                    case 'cyan': return 'bg-cyan-100 border border-cyan-300 text-cyan-800 dark:bg-cyan-900/40 dark:border-cyan-600 dark:text-cyan-200 shadow-lg ring-2 ring-cyan-200 dark:ring-cyan-700';
                    case 'green': return 'bg-green-100 border border-green-300 text-green-800 dark:bg-green-900/40 dark:border-green-600 dark:text-green-200 shadow-lg ring-2 ring-green-200 dark:ring-green-700';
                    case 'teal': return 'bg-teal-100 border border-teal-300 text-teal-800 dark:bg-teal-900/40 dark:border-teal-600 dark:text-teal-200 shadow-lg ring-2 ring-teal-200 dark:ring-teal-700';
                    case 'slate': return 'bg-slate-100 border border-slate-300 text-slate-800 dark:bg-slate-900/40 dark:border-slate-600 dark:text-slate-200 shadow-lg ring-2 ring-slate-200 dark:ring-slate-700';
                    case 'gray': return 'bg-gray-100 border border-gray-300 text-gray-800 dark:bg-gray-900/40 dark:border-gray-600 dark:text-gray-200 shadow-lg ring-2 ring-gray-200 dark:ring-gray-700';
                    default: return 'bg-slate-100 border border-slate-300 text-slate-800 dark:bg-slate-900/40 dark:border-slate-600 dark:text-slate-200';
                  }
                } else {
                  return 'hover:bg-teal-900 hover:bg-opacity-10 transition-colors';
                }
              };
              
              return (
                <div 
                  key={tier.name}
                  className={`flex items-center gap-3 p-4 rounded-xl transition-all duration-300 ${getColorClasses(tier.color, isSelected)}`}
                >
                  <IconComponent className={`w-6 h-6 ${isSelected ? 'opacity-100' : 'opacity-70'}`} />
                  <div>
                    <div className={`font-semibold ${isSelected ? 'font-bold' : ''}`}>
                      {tier.name}
                    </div>
                    <div className={`text-sm ${isSelected ? 'opacity-90' : 'opacity-70'}`}>
                      {tier.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right side - Heatmap matrix */}
        <div className="lg:w-2/3">
          <div className="relative">
            {/* Y-axis label */}
            <div className="absolute -left-16 top-1/2 -translate-y-1/2 -rotate-90 text-sm font-medium text-slate-600 dark:text-slate-400">
              Strategic maturity
            </div>
            
            {/* Enhanced Y-axis numbers with bold major ticks */}
            <div className="absolute -left-8 text-xs text-slate-500 dark:text-slate-400">
              {/* Strategic values 25 down to 6, each row is 32px (h-8) */}
              <div className={`absolute leading-none font-bold text-slate-700 dark:text-slate-300`} style={{ top: `${(25-25) * 32 + 12}px` }}>25</div>
              <div className="absolute leading-none" style={{ top: `${(25-22) * 32 + 12}px` }}>22</div>
              <div className="absolute leading-none" style={{ top: `${(25-18) * 32 + 12}px` }}>18</div>
              <div className="absolute leading-none" style={{ top: `${(25-14) * 32 + 12}px` }}>14</div>
              <div className={`absolute leading-none font-bold text-slate-700 dark:text-slate-300`} style={{ top: `${(25-10) * 32 + 12}px` }}>10</div>
              <div className="absolute leading-none" style={{ top: `${(25-6) * 32 + 12}px` }}>6</div>
            </div>

            {/* Main heatmap grid - Simplified with subtle gridlines */}
            <div 
              data-testid="maturity-heatmap"
              className="border border-slate-400 dark:border-slate-500 rounded-2xl overflow-hidden bg-slate-100 dark:bg-slate-800 shadow-lg"
            >
              {heatmapGrid.map((row, rowIndex) => (
                <div key={rowIndex} className="flex">
                  {row.map((cell, cellIndex) => (
                    <div
                      key={`${cell.opX}-${cell.stratY}`}
                      className={`
                        w-10 h-8 relative transition-all duration-200 border border-white/10
                        ${cell.bgColor} hover:opacity-80 cursor-pointer
                      `}
                      title={`Op: ${cell.opX}, Strat: ${cell.stratY} - ${cell.tier}`}
                      onMouseEnter={() => {
                        if (cell.isCurrentPosition && !printMode) {
                          setShowTooltip(true);
                          setMousePosition({ x: cell.opX, y: cell.stratY });
                        }
                      }}
                      onMouseLeave={() => setShowTooltip(false)}
                    >
                      {cell.isCurrentPosition && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          {/* Position marker - animated in normal mode, static in print mode */}
                          <div className="relative flex h-4 w-4">
                            {!printMode && (
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                            )}
                            <span className="relative inline-flex rounded-full h-4 w-4 bg-white border-2 border-teal-500 shadow-lg"></span>
                          </div>
                          
                          {/* Position tooltip - only show in interactive mode */}
                          {showTooltip && !printMode && (
                            <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 bg-slate-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg z-10 whitespace-nowrap">
                              <div className="font-medium">Operational: {opScore}/30</div>
                              <div className="font-medium">Strategic: {stratScore}/25</div>
                              <div className="font-bold text-teal-300">Tier: {currentTier}</div>
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800"></div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Enhanced X-axis numbers with bold major ticks */}
            <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mt-2 px-1">
              {[6, 10, 14, 18, 22, 26, 30].map(num => (
                <div key={num} className={`text-center ${num % 10 === 0 ? 'font-bold text-slate-700 dark:text-slate-300' : ''}`}>
                  {num}
                </div>
              ))}
            </div>
            
            {/* X-axis label */}
            <div className="text-center text-sm font-medium text-slate-600 dark:text-slate-400 mt-2">
              Operational maturity
            </div>

            {/* Zone labels using percentage positioning to match grid zones */}
            <div className="absolute -top-6 left-0 right-0 pointer-events-none flex justify-between text-xs font-semibold px-4">
              <div className="text-white">Manual</div>
              <div className="text-white">Siloed</div>
              <div className="text-white    ">Managed</div>
              <div className="text-white    ">Controlled</div>
              <div className="text-white    ">Autonomous</div>
            </div>
          </div>

          {/* Current position indicator */}
          <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-600">
            <div className="text-center">
              <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">Your position:</div>
              <div className="font-bold text-lg text-slate-900 dark:text-white">
                {currentTier} ({opScore}/30, {stratScore}/25)
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}